import { useQuery } from '@tanstack/react-query';
import { fetchLegalStructureEntities } from '../../../services/queries/MaggieLegalStructureQueries';

/**
 * The legal record for every entity — `GET /legalStructure/entities`.
 *
 * **One definition, because the key is the dedupe.** Four callers read this register:
 * the structure chart's graph join, the chart's fund selector, the legal entity
 * registry table, and the detail panel. React Query collapses them into a single
 * request only while they agree on the query key, so a second hand-typed
 * `['legalStructureEntities']` anywhere would silently reintroduce a duplicate
 * network read — silently, because nothing fails when it happens.
 *
 * It lives here rather than beside any one caller so that no surface has to import
 * from another surface to use it. It was previously declared in the structure chart's
 * `useStructureChartGraph` and separately re-declared in the registry's
 * `useRegistryRows`; those two copies agreed, but nothing made them.
 *
 * Deliberately not keyed by asOf: `GET /legalStructure/entities` takes no parameters
 * (structure.controller.ts — `entities()`), so there is nothing to pin. The register
 * only decorates nodes the as-of-pinned graph query returns (names, kinds, roles) —
 * the date-sensitive facts (which edges exist, values) all come from `/graph` and
 * `/rollup`, which are pinned. Historical legal records ("the register as of Q3 2025")
 * is the structure-versioning work the spec defers to phase 2 (§12) and needs BE.
 */
export function useLegalStructureRegister(enabled = true) {
  return useQuery({
    queryKey: ['legalStructureEntities'],
    queryFn: fetchLegalStructureEntities,
    retry: false,
    // Callers outside the module (the portfolio grid's tree mode) mount on every tenant
    // and must not issue the request until the reader actually asks for structure.
    enabled,
  });
}
